/**
 * ============================================================================
 * VARIANT FILTER — Deterministische herpaginering (Padeldistrict, Enterprise/Clean Canvas)
 * ============================================================================
 *
 * DOEL
 * Bij actieve variant-filters (maat/kleur) toont het grid uitsluitend producten
 * die voor de gefilterde variant leverbaar zijn, met correcte paginering:
 *   - elk leverbaar product verschijnt PRECIES ÉÉN KEER over alle pagina's,
 *   - pagina N toont leverbaar product (N-1)*perPage+1 .. N*perPage,
 *   - de pagineringsbalk wordt teruggebracht tot het ECHTE aantal pagina's.
 *
 * AANPAK
 * Bouw één geordende, ontdubbelde lijst van leverbare producten door alle
 * serverpagina's via de Shopify Section Rendering API op te halen, en toon
 * daaruit de schijf die bij het huidige paginanummer hoort. Geen sessionStorage:
 * route-onafhankelijk, een gedeelde link naar pagina N toont hetzelfde als
 * doorklikken.
 *
 * Waarom dit duplicatie structureel uitsluit: er is één unieke lijst (ontdubbeld
 * op product-handle), dus een product kan niet op twee pagina's belanden,
 * ongeacht of het native staat of via de API is opgehaald, en ongeacht
 * verschuivingen in de best-selling-sortering tussen requests.
 *
 * DEBUG: zet CONFIG.debug op false voordat dit live gaat.
 * ============================================================================
 */

(function () {
  'use strict';

  const CONFIG = {
    gridSelector: '#filter-results ul.grid[data-variant-filter-section]',
    sectionAttr: 'data-variant-filter-section',
    unavailableAttr: 'data-variant-unavailable',
    productCardSelector: ':scope > li.js-pagination-result',
    productLinkSelector: 'a[href*="/products/"]',
    paginationSelector: '#filter-results .pagination',
    variantFilterParams: ['filter.v.option.maat', 'filter.v.m.plenty.kleur'],
    defaultProductsPerPage: 24,
    debounceMs: 100,
    hiddenClass: 'variant-filter--loading',
    maxServerPagesFallback: 50, // veiligheidsplafond als de balk onleesbaar is
    debug: false
  };

  // ---------------------------------------------------------------------------
  // STATE
  // ---------------------------------------------------------------------------
  let debounceTimer = null;
  let observer = null;
  let runToken = 0;
  let lastFilterKey = null;
  let activeAbortController = null;
  const pageCache = new Map(); // key: filterKey + '|' + page -> { available:[li], rawCount, maxPages }

  function log() {
    if (CONFIG.debug) console.log.apply(console, ['[VariantFilter]'].concat([].slice.call(arguments)));
  }

  // ---------------------------------------------------------------------------
  // URL / GRID HELPERS
  // ---------------------------------------------------------------------------
  function hasActiveVariantFilter() {
    const p = new URLSearchParams(location.search);
    return CONFIG.variantFilterParams.some(k => p.has(k));
  }

  function getFilterKey() {
    const p = new URLSearchParams(location.search);
    p.delete('page');
    p.sort();
    return location.pathname + '?' + p.toString();
  }

  function getCurrentPage() {
    return parseInt(new URLSearchParams(location.search).get('page'), 10) || 1;
  }

  function getProductsPerPage(grid) {
    return parseInt(grid.getAttribute('data-products-per-page'), 10) || CONFIG.defaultProductsPerPage;
  }

  function handleOf(li) {
    const a = li.querySelector(CONFIG.productLinkSelector);
    return a ? a.getAttribute('href').split('?')[0] : null;
  }

  // ---------------------------------------------------------------------------
  // ECHTE AANTAL SERVERPAGINA'S UIT DE PAGINERINGSBALK
  // ---------------------------------------------------------------------------
  function readServerMaxPagesFrom(doc) {
    const pag = doc.querySelector(CONFIG.paginationSelector);
    if (!pag) return 1;
    let max = 1;
    pag.querySelectorAll('a[href]').forEach(a => {
      try {
        const u = new URL(a.getAttribute('href'), location.origin);
        const n = parseInt(u.searchParams.get('page'), 10);
        if (n && n > max) max = n;
      } catch (e) { /* genegeerd */ }
    });
    pag.querySelectorAll('li, span, a').forEach(el => {
      const t = (el.textContent || '').trim();
      if (/^\d+$/.test(t)) {
        const n = parseInt(t, 10);
        if (n > max) max = n;
      }
    });
    return max;
  }

  // ---------------------------------------------------------------------------
  // ÉÉN SERVERPAGINA OPHALEN (met cache) -> leverbare product-li's
  // ---------------------------------------------------------------------------
  async function fetchServerPage(filterKey, page, sectionId, signal) {
    const cacheKey = filterKey + '|' + page;
    if (pageCache.has(cacheKey)) return pageCache.get(cacheKey);

    const url = new URL(location.href);
    url.searchParams.set('page', page);
    url.searchParams.set('section_id', sectionId);

    const res = await fetch(url.toString(), { signal });
    if (!res.ok) throw new Error('fetch faalde (' + res.status + ') op pagina ' + page);

    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const grid = doc.querySelector(CONFIG.gridSelector) || doc.querySelector('#filter-results ul.grid');

    const result = { available: [], rawCount: 0, maxPages: readServerMaxPagesFrom(doc) };
    if (grid) {
      const cards = grid.querySelectorAll(CONFIG.productCardSelector);
      result.rawCount = cards.length;
      cards.forEach(li => {
        const selfUnavailable = li.getAttribute(CONFIG.unavailableAttr) === 'true';
        const childUnavailable = li.querySelector('[' + CONFIG.unavailableAttr + '="true"]');
        if (!selfUnavailable && !childUnavailable) result.available.push(li);
      });
    }
    pageCache.set(cacheKey, result);
    return result;
  }

  // ---------------------------------------------------------------------------
  // GLOBALE, ONTDUBBELDE LIJST VAN LEVERBARE PRODUCTEN
  // ---------------------------------------------------------------------------
  async function buildAvailableList(filterKey, sectionId, signal) {
    let serverMax = Math.min(Math.max(readServerMaxPagesFrom(document), 1), CONFIG.maxServerPagesFallback);

    // Alle serverpagina's PARALLEL ophalen (veel sneller dan sequentieel).
    const fetchRange = (from, to) => Promise.all(
      Array.from({ length: (to - from + 1) }, (_, i) =>
        fetchServerPage(filterKey, from + i, sectionId, signal)
          .catch(() => ({ available: [], rawCount: 0, maxPages: 0 }))
      )
    );

    let pages = await fetchRange(1, serverMax);
    if (signal && signal.aborted) throw new DOMException('afgebroken', 'AbortError');

    // De pagineringsbalk op pagina 1 toont soms een venster (bijv. "1 2 >")
    // waardoor pagina's buiten dat venster niet worden meegeteld. Controleer
    // of een opgehaalde pagina via ZIJN balk een hogere maxPages meldt, en
    // haal die extra pagina's alsnog op. Herhaal tot er geen nieuwe pagina's
    // meer worden ontdekt.
    let discoveredMax = serverMax;
    let prevMax = 0;

    while (prevMax < discoveredMax) {
      prevMax = discoveredMax;
      pages.forEach(p => {
        if (p.maxPages > discoveredMax) discoveredMax = p.maxPages;
      });
      discoveredMax = Math.min(discoveredMax, CONFIG.maxServerPagesFallback);

      if (discoveredMax > serverMax) {
        log('balk op opgehaalde pagina meldt meer paginas: ' + discoveredMax + ' (initieel: ' + serverMax + ')');
        const extra = await fetchRange(serverMax + 1, discoveredMax);
        if (signal && signal.aborted) throw new DOMException('afgebroken', 'AbortError');
        pages = pages.concat(extra);
        serverMax = discoveredMax;
      }
    }

    // Eén geordende, op product-handle ontdubbelde lijst.
    const seen = new Set();
    const ordered = [];
    pages.forEach((p, idx) => {
      let nieuw = 0;
      p.available.forEach(li => {
        const h = handleOf(li);
        if (h && seen.has(h)) return; // ontdubbel over pagina's heen
        if (h) seen.add(h);
        ordered.push(li);
        nieuw++;
      });
      log('serverpagina ' + (idx + 1) + ': ' + p.rawCount + ' gematcht, ' +
          p.available.length + ' leverbaar, ' + nieuw + ' nieuw uniek');
    });

    return ordered;
  }

  // ---------------------------------------------------------------------------
  // GRID VULLEN MET DE JUISTE SCHIJF
  // ---------------------------------------------------------------------------
  function renderSlice(grid, sliceLis) {
    grid.querySelectorAll(CONFIG.productCardSelector).forEach(li => li.remove());
    const oldEmpty = grid.querySelector('.variant-filter-empty');
    if (oldEmpty) oldEmpty.remove();

    if (sliceLis.length === 0) {
      showEmptyState(grid);
      return;
    }
    const frag = document.createDocumentFragment();
    sliceLis.forEach(li => frag.appendChild(document.importNode(li, true)));
    grid.appendChild(frag);
  }

  function showEmptyState(grid) {
    const existing = grid.querySelector('.variant-filter-empty');
    if (existing) existing.remove();
    const li = document.createElement('li');
    li.className = 'variant-filter-empty col-span-full';
    const p = document.createElement('p');
    p.textContent = grid.getAttribute('data-empty-text') ||
      'Er zijn geen producten gevonden die op voorraad zijn voor de geselecteerde filters. Probeer een andere selectie of verwijder je filters.';
    li.appendChild(p);
    grid.appendChild(li);
  }

  // ---------------------------------------------------------------------------
  // PAGINERINGSBALK TERUGTRIMMEN NAAR HET ECHTE AANTAL PAGINA'S
  // ---------------------------------------------------------------------------
  // Afgestemd op de Clean Canvas markup:
  //   <nav aria-label="Paginering"> <ul class="pagination js-pagination">
  //     <li ...--arrow> <a class="pagination__arrow--prev" href="...page=N-1"> </li>
  //     <li ... md:hidden> Pagina 6 / 8 </li>            (mobiele teller)
  //     <li class="hidden md:block"> <a class="pagination__page-link" href="...page=1">1</a> </li>
  //     <li class="hidden md:block"> … </li>             (ellipsis)
  //     <li class="hidden md:block"> <span class="pagination__page-current">6</span> </li>
  //     <li ...--arrow> <a class="pagination__arrow--next" href="...page=N+1"> </li>
  //   </ul></nav>
  function rewritePagination(currentPage, realPages) {
    const pag = document.querySelector(CONFIG.paginationSelector);
    if (!pag) return;
    const nav = pag.closest('nav') || pag;

    if (realPages <= 1) {
      nav.style.display = 'none';
      return;
    }
    nav.style.display = '';

    const lis = () => Array.prototype.slice.call(pag.querySelectorAll(':scope > li'));

    // Vaste onderdelen vinden (pijlen + mobiele teller). De server levert de
    // nummers ingedikt aan met een "…", daarom HERBOUWEN we de nummerreeks
    // 1..realPages in plaats van te verbergen (verbergen kan ingedikte
    // middelste pagina's niet terughalen).
    const prevLi = lis().find(li => li.querySelector('a.pagination__arrow--prev'));
    const nextLi = lis().find(li => li.querySelector('a.pagination__arrow--next'));
    const mobileLi = lis().find(li => !li.querySelector('a') && /\d+\s*\/\s*\d+/.test((li.textContent || '').trim()));

    // Templates voor een nummerlink en de huidige-pagina-markering.
    const linkLi = lis().find(li => li.querySelector('a.pagination__page-link'));
    const currentLi = lis().find(li => li.querySelector('span.pagination__page-current'));
    if (!linkLi) return; // geen template om mee te bouwen, balk met rust laten

    const templateHref = linkLi.querySelector('a.pagination__page-link').getAttribute('href');
    const makeHref = (n) => {
      try {
        const u = new URL(templateHref, location.origin);
        u.searchParams.set('page', n);
        return u.pathname + u.search;
      } catch (e) { return templateHref; }
    };

    // Alle bestaande nummer-, huidige- en ellipsis-li's verwijderen,
    // pijlen en mobiele teller behouden.
    lis().forEach(li => {
      if (li === prevLi || li === nextLi || li === mobileLi) return;
      li.remove();
    });

    // Verse nummerreeks 1..realPages bouwen.
    const frag = document.createDocumentFragment();
    for (let n = 1; n <= realPages; n++) {
      let li;
      if (n === currentPage && currentLi) {
        li = currentLi.cloneNode(true);
        const span = li.querySelector('span.pagination__page-current');
        if (span) span.textContent = String(n);
      } else {
        li = linkLi.cloneNode(true);
        const a = li.querySelector('a.pagination__page-link');
        if (a) { a.setAttribute('href', makeHref(n)); a.textContent = String(n); }
      }
      li.style.display = '';
      frag.appendChild(li);
    }
    if (nextLi) pag.insertBefore(frag, nextLi);
    else pag.appendChild(frag);

    // Pijlen bijwerken.
    if (prevLi) prevLi.style.display = currentPage > 1 ? '' : 'none';
    if (nextLi) nextLi.style.display = currentPage < realPages ? '' : 'none';

    // Mobiele teller "Pagina X / Y" naar het echte aantal zetten.
    if (mobileLi) {
      mobileLi.textContent = (mobileLi.textContent || '').replace(/\d+\s*\/\s*\d+/, currentPage + ' / ' + realPages);
    }
  }

  // ---------------------------------------------------------------------------
  // HOOFDLOGICA
  // ---------------------------------------------------------------------------
  async function processGrid(grid) {
    const myToken = ++runToken;

    // Breek eventuele lopende fetches van de vorige run af.
    if (activeAbortController) activeAbortController.abort();
    const ac = new AbortController();
    activeAbortController = ac;

    const filterKey = getFilterKey();
    if (filterKey !== lastFilterKey) {
      pageCache.clear();
      lastFilterKey = filterKey;
    }
    const currentPage = getCurrentPage();
    const perPage = getProductsPerPage(grid);
    const sectionId = grid.getAttribute(CONFIG.sectionAttr) || 'main-collection-products';

    grid.classList.add(CONFIG.hiddenClass);
    try {
      const ordered = await buildAvailableList(filterKey, sectionId, ac.signal);
      if (myToken !== runToken) { log('verouderde run genegeerd'); return; }

      const total = ordered.length;
      const realPages = Math.max(1, Math.ceil(total / perPage));
      // Als de URL een hoger paginanummer heeft dan er na het filteren nog
      // bestaat, corrigeer de URL zodat deze consistent is met wat getoond wordt.
      const effectivePage = Math.min(currentPage, realPages);
      if (effectivePage !== currentPage) {
        const correctedUrl = new URL(location.href);
        if (effectivePage <= 1) correctedUrl.searchParams.delete('page');
        else correctedUrl.searchParams.set('page', effectivePage);
        history.replaceState(null, '', correctedUrl.pathname + correctedUrl.search);
      }
      const start = (effectivePage - 1) * perPage;
      const slice = ordered.slice(start, start + perPage);

      log('telling compleet: ' + total + ' leverbare producten, echte pagina\'s: ' + realPages +
          ', huidige pagina: ' + currentPage + ', getoond als pagina: ' + effectivePage + ', getoond: ' + slice.length);

      renderSlice(grid, slice);
      rewritePagination(effectivePage, realPages);
    } catch (e) {
      if (e && e.name === 'AbortError') { log('afgebroken'); return; }
      console.error('[VariantFilter] fout bij verwerken:', e);
    } finally {
      if (myToken === runToken) grid.classList.remove(CONFIG.hiddenClass);
    }
  }

  function run() {
    const grid = document.querySelector(CONFIG.gridSelector);
    if (!grid) return;
    if (!hasActiveVariantFilter()) return;
    processGrid(grid);
  }

  function debouncedRun() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(run, CONFIG.debounceMs);
  }

  // ---------------------------------------------------------------------------
  // MUTATION OBSERVER — vangt AJAX-filternavigatie (grid-vervanging) op.
  // Onze eigen renderSlice voegt alleen <li>'s toe aan het bestaande ul.grid en
  // triggert deze observer dus niet (die reageert op vervanging van ul.grid).
  // ---------------------------------------------------------------------------
  function setupObserver() {
    const target = document.getElementById('filter-results') ||
                   document.getElementById('main') ||
                   document.body;

    observer = new MutationObserver(function (mutations) {
      for (const mutation of mutations) {
        if (mutation.type !== 'childList' || mutation.addedNodes.length === 0) continue;
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;
          if ((node.matches && node.matches('ul.grid')) ||
              (node.querySelector && node.querySelector('ul.grid'))) {
            debouncedRun();
            return;
          }
        }
      }
    });

    observer.observe(target, { childList: true, subtree: true });
  }

  // ---------------------------------------------------------------------------
  // INIT
  // ---------------------------------------------------------------------------
  function init() {
    run();
    setupObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();